import { ComponentProps, forwardRef, ReactElement, ReactNode } from 'react';
import styled from '@emotion/styled';
import { IUIComponentProps } from '../../types';
import { Input } from '../Input';
import { ITheme, themed } from '../../themer';
import { Responsive } from '../../utils';

export interface IBaseTextFieldProps
	extends IUIComponentProps,
		Omit<ComponentProps<typeof Input>, 'ref'> {
	error?: string | ReactElement;
	warning?: string | ReactElement;
	invalid?: boolean;
	label?: string;
	ariaLabel?: string;
	suffixContent?: ReactNode;
	hideErrorMessage?: boolean;
}

export type ITextFieldProps = IBaseTextFieldProps &
	({ label: string } | { ariaLabel: string });

export const StyledTextFieldLabel = styled.label<{}, ITheme>`
	font-size: ${themed(({ typography }) => typography.header.h5.size)};

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		font-size: ${themed(({ typography }) => typography.mobile.small.size)};
		font-weight: bold;
	}
`;

export const StyledTextFieldLabelText = styled.span`
	display: block;
	margin-bottom: 8px;
`;

export const StyledTextField = styled.div``;

export const StyledTextFieldErrorMessage = styled.div<{}, ITheme>`
	color: ${themed(({ color }) => color.state.error)};
	padding-left: ${({ theme: { textField } }) =>
		textField.errorMessage.paddingLeft};
	font-size: ${({ theme: { textField } }) => textField.errorMessage.fontSize};
	padding-top: ${({ theme: { textField } }) =>
		textField.errorMessage.paddingTop};
`;

export const StyledTextFieldWarningMessage = styled(
	StyledTextFieldErrorMessage,
)`
	color: #ff791f;
`;

export const StyledTextFieldInputWrapper = styled.div`
	position: relative;

	svg {
		path {
			fill: ${themed(({ input }) => input.icon.background)};
		}
	}
`;

export const TextField = forwardRef<HTMLInputElement, ITextFieldProps>(
	(
		{
			label,
			error,
			warning,
			invalid,
			className,
			ariaLabel,
			suffixContent,
			hideErrorMessage,
			...rest
		}: ITextFieldProps,
		ref,
	) => {
		const hasError = !!error && !hideErrorMessage;
		const hasWarning = !!warning;
		const shouldShowWarning = hasWarning && !hasError;

		const InputElement = (
			<Input
				ref={ref}
				invalid={invalid ?? hasError}
				aria-invalid={invalid ?? hasError}
				aria-label={ariaLabel ?? label}
				hasWarning={shouldShowWarning}
				{...rest}
			/>
		);

		const ContentElement = suffixContent ? (
			<StyledTextFieldInputWrapper>
				{InputElement}
				{suffixContent}
			</StyledTextFieldInputWrapper>
		) : (
			InputElement
		);

		return (
			<StyledTextField className={className}>
				{label ? (
					<StyledTextFieldLabel>
						<StyledTextFieldLabelText>{label}</StyledTextFieldLabelText>
						{ContentElement}
					</StyledTextFieldLabel>
				) : (
					ContentElement
				)}
				{hasError && (
					<StyledTextFieldErrorMessage role="alert">
						{error}
					</StyledTextFieldErrorMessage>
				)}
				{shouldShowWarning && (
					<StyledTextFieldWarningMessage role="alert">
						{warning}
					</StyledTextFieldWarningMessage>
				)}
			</StyledTextField>
		);
	},
);
